import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ManageCredits.css';
import { apiClient } from './config';

const ManageCredits = () => {
    const navigate = useNavigate();
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [, setCurrentTime] = useState(Date.now());

    useEffect(() => {
        fetchCredits();
        // Auto-refresh every 30 seconds so CLI usage shows up quickly
        const interval = setInterval(fetchCredits, 30000);
        // Also refresh when the tab regains focus (user switches back from CLI)
        const onFocus = () => fetchCredits();
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update current time every second to refresh countdown timers in real-time
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000); // Update every second for smooth countdown
        return () => clearInterval(timer);
    }, []);

    const fetchCredits = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await apiClient.get('/api/credits');

            if (response.status === 401) {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                navigate('/login');
                return;
            }

            if (response.data.success) {
                setCredits(response.data.credits);
                setError(null);
            } else {
                setError(response.data.error || 'Failed to fetch credits');
            }
        } catch (err) {
            console.error('Error fetching credits:', err);
            setError('Failed to load credit information');
        } finally {
            setLoading(false);
        }
    };

    const formatResetTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        const resetDate = new Date(timestamp * 1000);
        const now = new Date();
        const diff = resetDate - now;

        if (diff < 0) return 'Resetting soon...';

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        } else {
            return `${seconds}s`;
        }
    };

    const getProgressBarColor = (remaining, limit) => {
        if (remaining === 'unlimited') return '#22d3ee';
        const percentage = (remaining / limit) * 100;
        if (percentage > 50) return '#06b6d4';
        if (percentage > 20) return '#fbbf24';
        return '#f87171';
    };

    const renderCreditCard = (title, icon, dailyInfo, hourlyInfo = null, isActive = true) => {
        const isDailyUnlimited = dailyInfo.remaining === 'unlimited';
        const isHourlyUnlimited = hourlyInfo?.remaining === 'unlimited';

        return (
            <div className="credit-card" style={{ opacity: isActive ? 1 : 0.5 }}>
                <div className="credit-card-header">
                    <span className="credit-icon">{icon}</span>
                    <h3>
                        {title}
                        {!isActive && <span style={{ fontSize: '0.7em', color: '#67e8f9', marginLeft: '8px' }}>Coming Soon</span>}
                    </h3>
                </div>
                
                {/* Daily Limit */}
                <div className="credit-section">
                    <div className="credit-label">Daily Limit</div>
                    <div className="credit-stats">
                        <div className="credit-numbers">
                            <span className="credit-remaining">
                                {isDailyUnlimited ? '∞' : dailyInfo.remaining}
                            </span>
                            <span className="credit-separator">/</span>
                            <span className="credit-limit">
                                {isDailyUnlimited ? '∞' : dailyInfo.limit}
                            </span>
                        </div>
                        {!isDailyUnlimited && (
                            <div className="credit-used">
                                {dailyInfo.used} used
                            </div>
                        )}
                    </div>
                    
                    {!isDailyUnlimited && (
                        <>
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ 
                                        width: `${(dailyInfo.remaining / dailyInfo.limit) * 100}%`,
                                        backgroundColor: getProgressBarColor(dailyInfo.remaining, dailyInfo.limit)
                                    }}
                                />
                            </div>
                            <div className="reset-time">
                                Resets in: {formatResetTime(dailyInfo.reset_time)}
                            </div>
                        </>
                    )}
                    {isDailyUnlimited && (
                        <div className="unlimited-badge">
                            ✨ Unlimited Access
                        </div>
                    )}
                </div>

                {/* Hourly Limit (if available) */}
                {hourlyInfo && (
                    <div className="credit-section hourly">
                        <div className="credit-label">Hourly Limit</div>
                        <div className="credit-stats">
                            <div className="credit-numbers">
                                <span className="credit-remaining">
                                    {isHourlyUnlimited ? '∞' : hourlyInfo.remaining}
                                </span>
                                <span className="credit-separator">/</span>
                                <span className="credit-limit">
                                    {isHourlyUnlimited ? '∞' : hourlyInfo.limit}
                                </span>
                            </div>
                            {!isHourlyUnlimited && (
                                <div className="credit-used">
                                    {hourlyInfo.used} used
                                </div>
                            )}
                        </div>
                        
                        {!isHourlyUnlimited && (
                            <>
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill" 
                                        style={{ 
                                            width: `${(hourlyInfo.remaining / hourlyInfo.limit) * 100}%`,
                                            backgroundColor: getProgressBarColor(hourlyInfo.remaining, hourlyInfo.limit)
                                        }}
                                    />
                                </div>
                                <div className="reset-time">
                                    Resets in: {formatResetTime(hourlyInfo.reset_time)}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="manage-credits-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading your usage limits...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="manage-credits-container">
                <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    <p>{error}</p>
                    <button className="retry-btn" onClick={fetchCredits}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-credits-container">
            <div className="credits-header">
                <button className="back-btn" onClick={() => navigate('/')}>
                    ← Back to Home
                </button>
                <h1>Usage Limits</h1>
                {credits?.is_admin && (
                    <div className="admin-badge-header">
                        <span className="admin-crown">👑</span>
                        Admin Account
                    </div>
                )}
            </div>

            <div className="credits-info-banner">
                <div className="banner-icon">ℹ️</div>
                <div className="banner-content">
                    <h3>Free Beta Access</h3>
                    <p>
                        During our beta period, all users get free daily limits: <strong>5 resume tailorings</strong>,{' '}
                        <strong>10 auto-apply jobs</strong>, and <strong>20 job searches</strong> per day.
                        All limits reset automatically at <strong>midnight UTC</strong>.
                        Approved bug bounty reports permanently increase your max limits.
                        {credits?.is_admin && ' As an admin, you have unlimited access to all features!'}
                    </p>
                </div>
            </div>

            <div className="credits-grid">
                {credits?.resume_tailoring && renderCreditCard(
                    'Resume Tailoring',
                    '📄',
                    credits.resume_tailoring.daily,
                    null,
                    true
                )}

                {credits?.job_applications && renderCreditCard(
                    'Auto Apply',
                    '🤖',
                    credits.job_applications.daily,
                    null,
                    true
                )}

                {credits?.job_search && renderCreditCard(
                    'Job Search',
                    '🔍',
                    credits.job_search.daily,
                    null,
                    true
                )}
            </div>

            <div className="credits-actions">
                <button className="refresh-btn" onClick={fetchCredits}>
                    Refresh Limits
                </button>
            </div>

            <div className="credits-faq">
                <h2>Frequently Asked Questions</h2>
                <div className="faq-item">
                    <h4>Is this service really free?</h4>
                    <p>
                        Yes! During our beta period, all features are completely free. Daily limits help us manage
                        API costs and ensure fair usage for all beta testers while we refine the platform.
                    </p>
                </div>
                <div className="faq-item">
                    <h4>When do my daily limits reset?</h4>
                    <p>
                        All daily limits reset at <strong>midnight UTC</strong> every day -
                        regardless of when you first used them.
                        The countdown timer shows exactly how long until the next reset.
                    </p>
                </div>
                <div className="faq-item">
                    <h4>What happens if I reach my daily limit?</h4>
                    <p>
                        You'll need to wait until your limits reset. The countdown timer shows
                        exactly when that will happen. Plan your job applications accordingly!
                    </p>
                </div>
                <div className="faq-item">
                    <h4>Can I get higher limits?</h4>
                    <p>
                        Yes. During beta, approved bug reports can permanently increase your limits.
                        Open the <strong>Report Bug + Beta Feedback</strong> page from Home to submit.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ManageCredits;

